import { DiscoveryErrorCode, DiscoveryRefusal } from '../types/discovery.types';

/**
 * Reading what the step 2 → 3 exit answered. Extracting a BOM is free; the
 * BOM is charged — one rate per component — when the buyer starts supplier
 * discovery. A refusal carries real numbers (the lines, their cost, the
 * balance, how many the balance covers) so the screen can say exactly why
 * nothing started and what the buyer can do instead.
 */

const isNumber = (value: unknown): value is number => typeof value === 'number';

interface ErrorBody {
    error?: unknown;
    message?: unknown;
}

/** The JSON body of a rejected axios call, if it had one. */
const responseBody = (error: unknown): ErrorBody | undefined =>
    (error as { response?: { data?: ErrorBody } } | null)?.response?.data;

/** `error` of the body when it is the `{ code, message, ... }` object form. */
const errorDetail = (error: unknown): Record<string, unknown> | undefined => {
    const detail = responseBody(error)?.error;
    return detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : undefined;
};

/**
 * The 402 INSUFFICIENT_CREDITS out of a failed exit, or null when the failure
 * was something else. A refusal missing its numbers is treated as "not a
 * refusal": the generic message is more honest than a half-filled picker.
 */
export const readDiscoveryRefusal = (error: unknown): DiscoveryRefusal | null => {
    const detail = errorDetail(error);
    if (!detail || detail.code !== 'INSUFFICIENT_CREDITS') return null;
    if (!isNumber(detail.components) || !isNumber(detail.cost) || !isNumber(detail.rate)) return null;
    return {
        message: typeof detail.message === 'string' ? detail.message : '',
        components: detail.components,
        cost: detail.cost,
        balance: isNumber(detail.balance) ? detail.balance : null,
        affordable: isNumber(detail.affordable) ? detail.affordable : null,
        rate: detail.rate,
    };
};

/** The refusal code of a failed exit, when the backend named one. */
export const discoveryErrorCode = (error: unknown): DiscoveryErrorCode | null => {
    const code = errorDetail(error)?.code;
    return typeof code === 'string' ? (code as DiscoveryErrorCode) : null;
};

/**
 * Robbie's sentence for each refusal: what did not happen, and what to do.
 * The backend's own sentences are written for an API reader ("rfqId is
 * required"), so the codes the buyer can hit are restated here.
 */
const MESSAGES: Record<DiscoveryErrorCode, string> = {
    INVALID_REQUEST:
        "I couldn't tell which RFQ this is, so I haven't started discovery. Reload the page and press Continue again.",
    INVALID_SELECTION:
        "I couldn't read which components you picked, so I haven't started discovery. Pick them again, then press Start.",
    UNKNOWN_COMPONENT:
        "This BOM changed since you last looked — one of the lines isn't on it any more, so I haven't started discovery. Reopen the BOM to check it, then press Continue again.",
    BOM_EMPTY:
        'This BOM has no lines left, so there is nothing for me to search for. Open the BOM and add a line, or go back and upload your file again.',
    BOM_NOT_FOUND:
        "I can't find an extracted BOM on this RFQ, so I haven't started discovery. Go back to Upload BOM and press Continue there to extract it.",
    FEATURE_DISABLED:
        "Supplier discovery is switched off for your org, so I haven't started. Ask an admin to turn it on, then press Continue again.",
};

const FALLBACK =
    "I couldn't start supplier discovery just now. Press Continue again in a moment — a retry with the same lines won't charge you twice.";

/** The codes' sentence when there is one, else the backend's buyer-facing `message`, else `fallback`. */
const sentenceFor = (error: unknown, table: Partial<Record<DiscoveryErrorCode, string>>, fallback: string): string => {
    const code = discoveryErrorCode(error);
    if (code && table[code]) return table[code] as string;
    // 500 and 502 carry a buyer-facing `message`. Anything else (a network
    // failure, a bare status) gets Robbie's line rather than axios's.
    const sentence = errorDetail(error)?.message || responseBody(error)?.message;
    return typeof sentence === 'string' && sentence ? sentence : fallback;
};

/** One line for any failed exit other than the credit refusal. */
export const discoveryErrorMessage = (error: unknown): string => sentenceFor(error, MESSAGES, FALLBACK);

/**
 * The entry gate's own refusals. The exit's sentences talk about starting
 * discovery and pressing Continue, which is not what happened here: the buyer
 * was keeping lines, and nothing was charged.
 */
const SELECTION_MESSAGES: Partial<Record<DiscoveryErrorCode, string>> = {
    INVALID_REQUEST: "I couldn't tell which RFQ this is, so I haven't changed the BOM. Reload the page and try again.",
    INVALID_SELECTION:
        "I couldn't read which components you picked, so I haven't changed the BOM. Pick them again and try once more.",
    UNKNOWN_COMPONENT:
        "This BOM changed since you opened it — one of your picks isn't on it any more, so I haven't changed anything. I've reloaded the lines; pick again.",
    BOM_EMPTY: 'This BOM has no lines left. Go back to upload and add your file again.',
    BOM_NOT_FOUND:
        "I can't find an extracted BOM on this RFQ. Go back to Upload BOM and press Continue there to extract it.",
};

const SELECTION_FALLBACK =
    "I couldn't keep those lines just now, and nothing was charged. Try again in a moment.";

/** One line for any failed `POST /trp/bom/select`. */
export const selectionErrorMessage = (error: unknown): string =>
    sentenceFor(error, SELECTION_MESSAGES, SELECTION_FALLBACK);
